import type { Dispatch, ReactNode } from "react"

import type { SegmentAnalysisAction } from "../hooks/use-segment-analysis"
import type {
  Segment,
  SegmentAnalysisDataSource,
  SegmentAnalysisRole,
  SegmentClaimRegistry,
  SegmentDetailTab,
  SegmentPendingChangesPayload,
  SegmentReport,
  SegmentRun,
} from "../types"
import type { EntityViewProps } from "./segment-people-tabs"

import { cn } from "@/lib/utils"

import { BulletList, SectionCard } from "./segment-detail-primitives"
import { MarketTab } from "./segment-market-tab"
import { CompetitorsTab, CustomersTab } from "./segment-people-tabs"

type SegmentDetailTabsProps = EntityViewProps & {
  detailTab: SegmentDetailTab
  /** User-hidden tabs (#2069 D2); "market" is never hideable. */
  hiddenTabs?: SegmentDetailTab[]
  /** Live vs mock data source — gates the Customers-tab CCP summary blurb fetch. */
  dataSource?: SegmentAnalysisDataSource
  dispatch: Dispatch<SegmentAnalysisAction>
  pendingChanges?: SegmentPendingChangesPayload | null
  claimRegistry?: SegmentClaimRegistry | null
  report: SegmentReport | null
  role: SegmentAnalysisRole
  segment: Segment
  /** Opens a CompanyDossier (Recommended-actions deep links). */
  onAnalyzeCompany?: (companyName: string) => void
  /** Opens a customer's Customer Centric Plan (Customers tab CTA). */
  onOpenCcp?: (companyName: string, options?: { autoDraft?: boolean }) => void
  /** Opens a competitor's Competitor Plan (Competitors tab CTA). */
  onOpenCompetitorPlan?: (companyName: string) => void
  onOpenClaim?: (claimId: string) => void
}

// Which report sections feed each tab — used to badge tabs with pending changes (§5.4).
const TAB_SECTIONS: Record<SegmentDetailTab, string[]> = {
  market: ["summary", "marketOverview", "trendAndInnovation"],
  customers: ["customerAnalysis"],
  competitors: ["competitorAnalysis"],
  swot: [],
  actions: [],
  history: [],
}

type TabBodyContext = EntityViewProps & {
  dataSource?: SegmentAnalysisDataSource
  dispatch: Dispatch<SegmentAnalysisAction>
  report: SegmentReport | null
  role: SegmentAnalysisRole
  segment: Segment
  tab: SegmentDetailTab
  onAnalyzeCompany?: (companyName: string) => void
  onOpenCcp?: (companyName: string, options?: { autoDraft?: boolean }) => void
  onOpenCompetitorPlan?: (companyName: string) => void
  claimRegistry?: SegmentClaimRegistry | null
  onOpenClaim?: (claimId: string) => void
}

const tabs: Array<{ id: SegmentDetailTab; label: string }> = [
  { id: "market", label: "Market overview" },
  { id: "customers", label: "Customers" },
  { id: "competitors", label: "Competitors" },
  { id: "history", label: "History" },
]

export function SegmentDetailTabs({
  canFavourite,
  companyClassifications,
  customerFilter,
  dataSource,
  detailTab,
  hiddenTabs,
  claimRegistry,
  dispatch,
  entitySearch,
  favouriteCompanies,
  onReclassify,
  onToggleFavourite,
  pendingChanges,
  report,
  role,
  segment,
  onAnalyzeCompany,
  onOpenCcp,
  onOpenCompetitorPlan,
  onOpenClaim,
}: SegmentDetailTabsProps) {
  const pendingSections = new Set(
    (pendingChanges?.changes ?? [])
      .filter((change) => change.status === "pending")
      .map((change) => change.section)
  )

  // #2069 D2: user-hidden tabs disappear ("market" is never hideable). If the
  // active tab got hidden (or a default-tab pref points at one), fall back to
  // Market overview rather than rendering headerless content.
  const hidden = new Set(hiddenTabs ?? [])
  const visibleTabs = tabs.filter((tab) => tab.id === "market" || !hidden.has(tab.id))
  const activeTab = visibleTabs.some((tab) => tab.id === detailTab) ? detailTab : "market"

  return (
    <section className="grid gap-4">
      <div className="border-border flex gap-1 overflow-x-auto border-b">
        {visibleTabs.map((tab) => {
          const hasPending = TAB_SECTIONS[tab.id].some((section) => pendingSections.has(section))
          return (
            <button
              key={tab.id}
              type="button"
              className={cn(
                "inline-flex h-10 shrink-0 items-center gap-1.5 border-b-2 px-3 text-sm font-medium transition",
                activeTab === tab.id
                  ? "border-primary text-primary"
                  : "text-muted-foreground hover:text-foreground border-transparent"
              )}
              onClick={() => dispatch({ type: "setDetailTab", tab: tab.id })}
            >
              {tab.label}
              {hasPending ? (
                <span
                  aria-label="has pending changes"
                  className="bg-warning inline-block size-1.5 rounded-full"
                />
              ) : null}
            </button>
          )
        })}
      </div>
      <div key={activeTab} className="animate-in fade-in-0 slide-in-from-bottom-1 duration-200">
        {renderTabBody({
          canFavourite,
          companyClassifications,
          customerFilter,
          dataSource,
          dispatch,
          entitySearch,
          favouriteCompanies,
          onReclassify,
          onToggleFavourite,
          report,
          role,
          segment,
          tab: activeTab,
          onAnalyzeCompany,
          onOpenCcp,
          onOpenCompetitorPlan,
          claimRegistry,
          onOpenClaim,
        })}
      </div>
    </section>
  )
}

function renderTabBody({
  canFavourite,
  companyClassifications,
  customerFilter,
  dataSource,
  dispatch,
  entitySearch,
  favouriteCompanies,
  onReclassify,
  onToggleFavourite,
  report,
  role,
  segment,
  tab,
  onAnalyzeCompany,
  onOpenCcp,
  onOpenCompetitorPlan,
  claimRegistry,
  onOpenClaim,
}: TabBodyContext): ReactNode {
  void role
  // onOpenCcp/onOpenCompetitorPlan carry the per-tab handlers now; the generic
  // onAnalyzeCompany stays in TabBodyContext for prop-drilling symmetry with
  // SegmentDetailTabsProps but is unused in this render path.
  void onAnalyzeCompany
  const entityView: EntityViewProps = {
    canFavourite,
    companyClassifications,
    customerFilter,
    entitySearch,
    favouriteCompanies,
    onReclassify,
    onToggleFavourite,
  }
  switch (tab) {
    case "market":
      return (
        <MarketTab
          claimRegistry={claimRegistry}
          report={report}
          segment={segment}
          onOpenClaim={onOpenClaim}
        />
      )
    case "customers":
      return (
        <CustomersTab
          dataSource={dataSource}
          dispatch={dispatch}
          entityView={entityView}
          report={report}
          segment={segment}
          onAnalyzeCompany={onOpenCcp}
        />
      )
    case "competitors":
      return (
        <CompetitorsTab
          dispatch={dispatch}
          entityView={entityView}
          report={report}
          segment={segment}
          onAnalyzeCompany={onOpenCompetitorPlan}
        />
      )
    case "swot":
    case "actions":
      return (
        <MarketTab
          claimRegistry={claimRegistry}
          report={report}
          segment={segment}
          onOpenClaim={onOpenClaim}
        />
      )
    case "history":
      return <HistoryTab report={report} />
    default: {
      const _exhaustive: never = tab
      void _exhaustive
      return null
    }
  }
}

function decisionViaLabel(via: SegmentRun["decidedVia"]): string {
  if (via === "admin_override") return " (admin override)"
  if (via === "auto") return " (auto)"
  return ""
}

/** "2026-05-07T10:00:00Z" → "7 May 2026, 10:00". Falls back to the raw string. */
function formatRunDate(iso: string): string {
  if (!iso) return ""
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return iso
  return date.toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })
}

function HistoryTab({ report }: { report: SegmentReport | null }) {
  const history = report?.history ?? [
    {
      runId: "no-backend-run-yet",
      generatedAt: "",
      changes: ["No completed backend report is available for this segment yet."],
    },
  ]

  return (
    <SectionCard title="Run history">
      <div className="grid gap-3">
        {history
          .slice()
          .reverse()
          .map((run) => {
            const when = formatRunDate(run.generatedAt)
            return (
              <article key={run.runId} className="bg-muted/50 rounded-md p-3">
                <h4 className="text-foreground text-sm font-semibold">{when || "Run"}</h4>
                {run.decidedBy ? (
                  <p className="text-muted-foreground text-xs">
                    Decided by {run.decidedBy}
                    {decisionViaLabel(run.decidedVia)}
                  </p>
                ) : null}
                <BulletList items={run.changes} />
                {run.runId && run.runId !== "no-backend-run-yet" ? (
                  <p className="text-muted-foreground/60 mt-2 font-mono text-[10px]">{run.runId}</p>
                ) : null}
              </article>
            )
          })}
      </div>
    </SectionCard>
  )
}
